import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { MapContainer, TileLayer, CircleMarker, Tooltip } from "react-leaflet";
import { Search, Plus, Trash2, Upload, Download, Compass } from "lucide-react";
import { toast } from "sonner";
import { get, post, patch, del, API, api, withToken } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { BulkAddCommunes } from "@/components/BulkAddCommunes";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

function Lookup({ onAdded }) {
  const [q, setQ] = useState("");
  const [res, setRes] = useState([]);
  const [sel, setSel] = useState([]);
  const search = async (e) => {
    e?.preventDefault();
    const isCp = /^\d{5}$/.test(q.trim());
    setRes(await get("/communes/lookup", isCp ? { cp: q.trim() } : { q }));
    setSel([]);
  };
  const add = async (codes) => {
    const r = await post("/communes", { insee_codes: codes });
    toast.success(`${r.added.length} commune(s) ajoutée(s) à la surveillance`);
    onAdded();
    search();
  };
  return (
    <div className="panel p-3">
      <div className="overline mb-2">Annuaire officiel (geo.api.gouv.fr) — recherche par nom ou code postal</div>
      <form onSubmit={search} className="flex gap-2">
        <Input data-testid="commune-lookup-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="ex. Genlis ou 21110" className="h-8 text-xs" />
        <Button data-testid="commune-lookup-btn" size="sm" className="h-8"><Search className="h-3.5 w-3.5 mr-1" />Rechercher</Button>
      </form>
      {res.length > 0 && (
        <div className="mt-2">
          <div className="max-h-60 overflow-y-auto">
            {res.map((c) => (
              <label key={c.insee} className="flex items-center gap-2 text-xs py-1 border-b border-slate-100 dark:border-slate-800">
                <input type="checkbox" data-testid={`lookup-select-${c.insee}`} disabled={c.monitored} checked={sel.includes(c.insee)} onChange={() => setSel(sel.includes(c.insee) ? sel.filter((x) => x !== c.insee) : [...sel, c.insee])} />
                <span className="font-medium">{c.name}</span>
                <span className="font-mono text-[10px] text-slate-500">{c.insee} · {c.postal_codes.join(", ")} · {c.department_code} · {c.population ?? "?"} hab.</span>
                {c.monitored ? <span className="ml-auto text-[10px] text-emerald-700 font-mono">SURVEILLÉE</span>
                  : <Button type="button" data-testid={`lookup-add-${c.insee}`} size="sm" variant="ghost" className="ml-auto h-6 text-[11px]" onClick={() => add([c.insee])}><Plus className="h-3 w-3" /></Button>}
              </label>
            ))}
          </div>
          <Button data-testid="lookup-add-selected" size="sm" className="mt-2 h-7 text-xs" disabled={!sel.length} onClick={() => add(sel)}>Ajouter la sélection ({sel.length})</Button>
        </div>
      )}
    </div>
  );
}

function Directory() {
  const qc = useQueryClient();
  const file = useRef();
  const [filter, setFilter] = useState("");
  const { data } = useQuery({ queryKey: ["communes"], queryFn: () => get("/communes") });
  const refresh = () => { qc.invalidateQueries({ queryKey: ["communes"] }); qc.invalidateQueries({ queryKey: ["communes-mon"] }); qc.invalidateQueries({ queryKey: ["dashboard"] }); };
  const upd = async (insee, body) => { await patch(`/communes/${insee}`, body); refresh(); };
  const remove = async (c) => { if (!window.confirm(`Retirer ${c.name} de l'annuaire ? (les événements déjà collectés sont conservés)`)) return; await del(`/communes/${c.insee}`); refresh(); };
  const importCsv = async (ev) => {
    const fd = new FormData();
    fd.append("file", ev.target.files[0]);
    const r = (await api.post("/communes/import", fd)).data;
    toast.success(`${r.added} commune(s) importée(s) sur ${r.rows} ligne(s)${r.unresolved.length ? ` — ${r.unresolved.length} non résolue(s)` : ""}`);
    ev.target.value = "";
    refresh();
  };
  const explore = async () => { const r = await post("/explore/run"); toast(r.started ? "Exploration lancée : découverte des sources du territoire…" : r.message); };
  const rows = (data || []).filter((c) => !filter || c.name.toLowerCase().includes(filter.toLowerCase()) || c.postal_codes.some((p) => p.startsWith(filter)));
  const pts = rows.filter((c) => c.lat);
  const center = pts.length ? [pts.reduce((a, c) => a + c.lat, 0) / pts.length, pts.reduce((a, c) => a + c.lon, 0) / pts.length] : [46.6, 2.4];
  return (
    <div className="grid grid-cols-1 [&>*]:min-w-0 xl:grid-cols-[1fr_420px] gap-4">
      <div className="space-y-3">
        <BulkAddCommunes onAdded={refresh} />
        <Lookup onAdded={refresh} />
        <div className="panel">
          <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-b border-slate-200 dark:border-slate-800">
            <h3 className="font-display text-sm font-bold">Communes de l'annuaire ({data?.length ?? 0})</h3>
            <Input data-testid="directory-filter" value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filtrer nom / CP" className="h-7 w-44 text-xs" />
            <div className="ml-auto flex flex-wrap gap-1.5">
              <Button data-testid="explore-btn" size="sm" className="h-7 text-xs" onClick={explore}><Compass className="h-3.5 w-3.5 mr-1" />Explorer les sources</Button>
              <input ref={file} type="file" accept=".csv,text/csv" hidden onChange={importCsv} data-testid="csv-import-input" />
              <Button data-testid="csv-import-btn" size="sm" variant="outline" className="h-7 text-xs" onClick={() => file.current.click()}><Upload className="h-3.5 w-3.5 mr-1" />Importer CSV</Button>
              <a data-testid="csv-export-btn" href={withToken(`${API}/communes/export`)} className="inline-flex items-center h-7 px-2 text-xs border border-slate-200 dark:border-slate-700 rounded-[3px]"><Download className="h-3.5 w-3.5 mr-1" />Exporter</a>
            </div>
          </div>
          <div className="text-[10px] text-slate-500 px-3 py-1">CSV accepté : colonne <code>insee</code> (ou <code>nom</code> + <code>code_postal</code>), séparateur « ; » ou « , ».</div>
          <div className="overflow-x-auto">
            <table className="dense-table w-full" data-testid="communes-table">
              <thead><tr><th>Commune</th><th>INSEE</th><th>Codes postaux</th><th>Dép.</th><th>Pop.</th><th>Territoires</th><th>Évts à venir</th><th>Sources</th><th>Surveillée</th><th>Active</th><th></th></tr></thead>
              <tbody>{rows.map((c) => (
                <tr key={c.insee} data-testid={`commune-row-${c.insee}`}>
                  <td className="font-medium">{c.name}</td><td className="font-mono">{c.insee}</td><td className="font-mono">{c.postal_codes.join(", ")}</td>
                  <td>{c.department_code}</td><td className="font-mono">{c.population ?? "—"}</td><td className="text-[11px]">{c.territories.join(", ") || "—"}</td>
                  <td className="font-mono">{c.upcoming_events}</td><td className="font-mono">{c.sources}</td>
                  <td><Switch data-testid={`commune-monitored-${c.insee}`} checked={c.monitored} onCheckedChange={(v) => upd(c.insee, { monitored: v })} /></td>
                  <td><Switch data-testid={`commune-active-${c.insee}`} checked={c.active} onCheckedChange={(v) => upd(c.insee, { active: v })} /></td>
                  <td><Button data-testid={`commune-delete-${c.insee}`} size="sm" variant="ghost" className="h-6" onClick={() => remove(c)}><Trash2 className="h-3.5 w-3.5 text-rose-600" /></Button></td>
                </tr>))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div className="panel isolate overflow-hidden h-[520px] xl:sticky xl:top-24" data-testid="communes-map">
        <MapContainer key={center.join(",")} center={center} zoom={pts.length ? 9 : 5} className="h-full w-full">
          <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          {pts.map((c) => (
            <CircleMarker key={c.insee} center={[c.lat, c.lon]} radius={6} pathOptions={{ color: c.monitored && c.active ? "#1d4ed8" : "#94a3b8", fillOpacity: 0.5 }}>
              <Tooltip>{c.name} · {c.upcoming_events} évt(s)</Tooltip>
            </CircleMarker>
          ))}
        </MapContainer>
      </div>
    </div>
  );
}

const TTYPES = [["circonscription", "Circonscription législative"], ["communaute_communes", "Communauté de communes"], ["metropole", "Métropole"], ["departement", "Département"], ["bassin_economique", "Bassin économique"], ["personnalise", "Personnalisé"]];

function Territories() {
  const qc = useQueryClient();
  const { data: list } = useQuery({ queryKey: ["territories"], queryFn: () => get("/territories") });
  const { data: communes } = useQuery({ queryKey: ["communes"], queryFn: () => get("/communes") });
  const [form, setForm] = useState({ name: "", type: "personnalise", insee_codes: [] });
  const [editing, setEditing] = useState(null);
  const refresh = () => { qc.invalidateQueries({ queryKey: ["territories"] }); qc.invalidateQueries({ queryKey: ["communes"] }); };
  const toggle = (code) => setForm({ ...form, insee_codes: form.insee_codes.includes(code) ? form.insee_codes.filter((x) => x !== code) : [...form.insee_codes, code] });
  const save = async () => {
    if (!form.name) return toast.error("Nom requis");
    if (editing) await patch(`/territories/${editing}`, form); else await post("/territories", form);
    toast.success("Territoire enregistré");
    setForm({ name: "", type: "personnalise", insee_codes: [] });
    setEditing(null);
    refresh();
  };
  return (
    <div className="grid lg:grid-cols-2 gap-4">
      <div className="panel p-3 space-y-2" data-testid="territory-form">
        <h3 className="font-display text-sm font-bold">{editing ? "Modifier le territoire" : "Nouveau territoire"}</h3>
        <Input data-testid="territory-name" placeholder="Nom (ex. Plaine dijonnaise)" className="h-8 text-xs" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <select data-testid="territory-type" className="w-full h-8 border border-slate-200 dark:border-slate-700 rounded-[3px] bg-transparent text-xs" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
          {TTYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <div className="overline">Communes ({form.insee_codes.length}) — une commune peut appartenir à plusieurs territoires</div>
        <div className="max-h-64 overflow-y-auto grid grid-cols-2 gap-x-3">
          {(communes || []).map((c) => (
            <label key={c.insee} className="flex items-center gap-1.5 text-xs py-0.5"><input type="checkbox" data-testid={`territory-commune-${c.insee}`} checked={form.insee_codes.includes(c.insee)} onChange={() => toggle(c.insee)} />{c.name}</label>
          ))}
        </div>
        <div className="flex gap-2"><Button data-testid="territory-save" size="sm" onClick={save}>Enregistrer</Button>
          {editing && <Button size="sm" variant="ghost" onClick={() => { setEditing(null); setForm({ name: "", type: "personnalise", insee_codes: [] }); }}>Annuler</Button>}</div>
      </div>
      <div className="panel">
        <div className="px-3 py-2 border-b border-slate-200 dark:border-slate-800 font-display text-sm font-bold">Territoires ({list?.length ?? 0})</div>
        {(list || []).map((t) => (
          <div key={t.id} className="px-3 py-2 border-b border-slate-100 dark:border-slate-800 flex items-center gap-2 text-xs" data-testid={`territory-${t.id}`}>
            <div className="flex-1"><div className="font-semibold">{t.name}</div><div className="text-[11px] text-slate-500">{TTYPES.find((x) => x[0] === t.type)?.[1]} · {t.insee_codes.length} commune(s)</div></div>
            <Button size="sm" variant="outline" className="h-6 text-[11px]" onClick={() => { setEditing(t.id); setForm({ name: t.name, type: t.type, insee_codes: t.insee_codes }); }}>Modifier</Button>
            <Button size="sm" variant="ghost" className="h-6" onClick={async () => { await del(`/territories/${t.id}`); refresh(); }}><Trash2 className="h-3.5 w-3.5 text-rose-600" /></Button>
          </div>
        ))}
        {!list?.length && <div className="px-3 py-6 text-center text-xs text-slate-400">Aucun territoire défini.</div>}
      </div>
    </div>
  );
}

export default function Communes() {
  return (
    <div className="p-4 lg:p-6">
      <h1 className="font-display text-xl font-bold mb-3">Communes & territoires</h1>
      <Tabs defaultValue="dir">
        <TabsList className="h-8"><TabsTrigger data-testid="tab-directory" value="dir" className="text-xs">Annuaire</TabsTrigger><TabsTrigger data-testid="tab-territories" value="terr" className="text-xs">Territoires</TabsTrigger></TabsList>
        <TabsContent value="dir" className="mt-3"><Directory /></TabsContent>
        <TabsContent value="terr" className="mt-3"><Territories /></TabsContent>
      </Tabs>
    </div>
  );
}
